import { useState } from "react";

import type { StyleSpecification } from "maplibre-gl";

import { MAP_STYLES } from "@/constants/map";
import { withBasePath } from "@/utils/basePath";

import MapMenuItemControl from "../menuItemControl";

export type BaseMapStyleKey = keyof typeof MAP_STYLES;

/**
 * Structural on purpose — the Redux copy of the selected basemap widens the
 * literal types in `MAP_STYLES`, and this control only reads these four fields.
 */
export type BaseMapStyle = {
  title: string;
  description: string;
  value: string | StyleSpecification;
  thumbnail?: string;
};

type Props<TKey extends BaseMapStyleKey> = {
  /** The basemaps this map may offer — callers narrow it to what they support. */
  styles: Record<TKey, BaseMapStyle>;
  selected: BaseMapStyle;
  onSelect: (key: TKey) => void;
};

/**
 * Basemap picker with no opinion on where the selection is stored — the global
 * map keeps it in Redux, a standalone map can keep it in local state. Generic
 * over the offered keys so a caller that only supports token-free styles gets
 * a correspondingly narrow `onSelect`.
 */
const BaseMapControl = <TKey extends BaseMapStyleKey>({
  styles,
  selected,
  onSelect
}: Props<TKey>) => {
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  const handleSelect = (key: TKey) => {
    setIsPickerOpen(false);
    onSelect(key);
  };

  const options = (Object.entries(styles) as [TKey, BaseMapStyle][]).filter(
    ([, style]) => style.value !== selected.value
  );

  return (
    <>
      <MapMenuItemControl
        aria-label={selected.title}
        tooltipContent={
          <div>
            <p className="mb-1 text-xs font-semibold">Basemap Selection</p>
            <p>Choose a style to adjust the map display</p>
          </div>
        }
        onClick={() => setIsPickerOpen(true)}
        className="relative mt-3 h-16 w-16 cursor-pointer overflow-hidden rounded-xl border border-neutral-200 shadow-[inset_0_4px_8px_rgba(0,0,0,0.2)] hover:border-teal-500 focus:outline-none"
      >
        <img
          src={withBasePath(selected.thumbnail ?? "")}
          alt=""
          className="h-full w-full object-cover"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
        <p className="absolute bottom-1 left-0 right-0 text-center text-[10px] text-white">
          {selected.title}
        </p>
      </MapMenuItemControl>

      {isPickerOpen && (
        <div className="absolute bottom-0 -left-2 flex -translate-x-full flex-row items-center gap-4 rounded-xl bg-white p-2">
          {options.map(([key, style]) => (
            <MapMenuItemControl
              key={key}
              aria-label={style.title}
              tooltipContent={
                <div>
                  <p className="mb-1 text-xs font-semibold">{style.title}</p>
                  <p>{style.description}</p>
                </div>
              }
              onClick={() => handleSelect(key)}
              tooltipPosition="top-center"
              className="h-12 w-12 overflow-hidden rounded-xl border border-neutral-200 bg-[linear-gradient(180deg,_rgba(0,0,0,0)_-48.39%,_rgba(0,0,0,0.2)_62.19%),_var(--background-bg-inversed,_#FFF)] cursor-pointer hover:border-teal-500"
            >
              <img
                src={withBasePath(style.thumbnail ?? "")}
                alt=""
                className="h-full w-full object-contain"
              />
            </MapMenuItemControl>
          ))}
        </div>
      )}
    </>
  );
};

export default BaseMapControl;
